import React, { useRef } from 'react'
import { Form, InputGroup, Row, Col } from 'react-bootstrap'
import { nullishValueToValue, nullishValueToInternalValue, toPosDisplayValue, toRightPosValue } from '../ArticleParse'
import type { Layoutable } from '../types'

type FormArea = {
  x?: number | string | null
  y?: number | string | null
  width?: number | string | null
  height?: number | string | null
  fromLeft?: boolean
}

type FormArticle = {
  areas?: FormArea[]
  [key: string]: unknown
}

type FormAreaErrors = {
  y?: string
  x?: string
  height?: string
  width?: string
  fromLeft?: string
}

type FormArticleErrors = {
  areas?: FormAreaErrors[]
}

type FormAreaTouched = {
  y?: boolean
  x?: boolean
  height?: boolean
  width?: boolean
  fromLeft?: boolean
}

type FormArticleTouched = {
  areas?: FormAreaTouched[]
}

export type AreaArticleAreaInputProps = {
  values: { article?: FormArticle }
  errors: { article?: FormArticleErrors }
  touched: { article?: FormArticleTouched }
  handleBlur: React.FocusEventHandler<HTMLElement>
  setFieldValue: (field: string, value: unknown) => void
  layoutable: Layoutable
}

export const AreaArticleAreaInput = ({
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  layoutable
}: AreaArticleAreaInputProps) => {
  const cachedX = useRef<number | string | null | undefined>(undefined)
  const cachedWidth = useRef<number | null>(null)

  return (
    <Row>
      <Form.Group as={Col} sm={3}>
        <Form.Label>天から</Form.Label>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            type='number'
            name='article.areas[0].y'
            value={nullishValueToValue(values.article?.areas?.[0]?.y)}
            placeholder='ナリ'
            onChange={(e) => {
              const areas = [{ ...values.article?.areas?.[0] }]
              areas[0].y = nullishValueToInternalValue(e.target.value)
              const newArticle = {
                ...values.article,
                areas
              }
              setFieldValue('article', newArticle)
            }}
            onBlur={handleBlur}
            isInvalid={!!(touched.article?.areas?.[0]?.y && errors.article?.areas?.[0]?.y)}
          />
          <InputGroup.Text>段目</InputGroup.Text>
          <Form.Control.Feedback type='invalid'>{errors.article?.areas?.[0]?.y}</Form.Control.Feedback>
        </InputGroup>
      </Form.Group>
      <Form.Group as={Col} sm={3}>
        <Form.Label as={Col}>{!values.article?.areas?.[0]?.fromLeft ? '右' : '左'}から</Form.Label>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            type='number'
            name='article.areas[0].x'
            value={toPosDisplayValue(layoutable, values?.article?.areas?.[0])}
            placeholder='ナリ'
            onChange={(e) => {
              const { numberOfRows } = layoutable
              const areas = [{ ...values.article?.areas?.[0] }]
              const internalValue = nullishValueToInternalValue(e.target.value)
              const fromLeft = values.article?.areas?.[0]?.fromLeft
              const areaWidth = values.article?.areas?.[0]?.width as number
              areas[0].x = fromLeft ? toRightPosValue(numberOfRows, areaWidth, internalValue) : internalValue
              const newArticle = {
                ...values.article,
                areas
              }
              setFieldValue('article', newArticle)
            }}
            onBlur={handleBlur}
            isInvalid={!!(touched.article?.areas?.[0]?.x && errors.article?.areas?.[0]?.x)}
          />
          <InputGroup.Text>行目</InputGroup.Text>
          <Form.Control.Feedback type='invalid'>{errors.article?.areas?.[0]?.x}</Form.Control.Feedback>
        </InputGroup>
        <Form.Check
          type='switch'
          id='fromLeftSwitch'
          label='左から'
          name='article.areas[0].fromLeft'
          value={String(values.article?.areas?.[0]?.fromLeft ?? false)}
          checked={values.article?.areas?.[0]?.fromLeft ?? false}
          onChange={(e) => {
            const areas = [{ ...values.article?.areas?.[0] }]
            areas[0].fromLeft = e.target.checked
            const newArticle = {
              ...values.article,
              areas
            }
            setFieldValue('article', newArticle)
          }}
          onBlur={handleBlur}
          isInvalid={!!(touched.article?.areas?.[0]?.fromLeft && errors.article?.areas?.[0]?.fromLeft)}
        />
      </Form.Group>
      <Form.Group as={Col} sm={3}>
        <Form.Label>天地</Form.Label>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            type='number'
            name='article.areas[0].height'
            value={parseInt(String(values.article?.areas?.[0]?.height ?? '')) || ''}
            onChange={(e) => {
              const areas = [{ ...values.article?.areas?.[0] }]
              areas[0].height = e.target.value
              const newArticle = {
                ...values.article,
                areas
              }
              setFieldValue('article', newArticle)
            }}
            onBlur={handleBlur}
            isInvalid={!!(touched.article?.areas?.[0]?.height && errors.article?.areas?.[0]?.height)}
          />
          <InputGroup.Text>段</InputGroup.Text>
          <Form.Control.Feedback type='invalid'>{errors.article?.areas?.[0]?.height}</Form.Control.Feedback>
        </InputGroup>
      </Form.Group>
      <Form.Group as={Col} sm={3}>
        <Form.Label as={Col}>左右</Form.Label>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            type='number'
            name='article.areas[0].width'
            value={parseInt(String(values.article?.areas?.[0]?.width ?? '')) || ''}
            onFocus={() => {
              cachedX.current = values.article?.areas?.[0]?.x
              cachedWidth.current = parseInt(String(values.article?.areas?.[0]?.width ?? ''))
            }}
            onChange={(e) => {
              const newWidth = parseInt(e.target.value)
              const oldWidth = cachedWidth.current
              const areas = [{ ...values.article?.areas?.[0] }]
              areas[0].width = e.target.value
              if (
                values.article?.areas?.[0]?.fromLeft &&
                cachedX.current != null &&
                cachedX.current !== '' &&
                oldWidth !== null &&
                !isNaN(oldWidth) &&
                !isNaN(newWidth)
              ) {
                areas[0].x = Number(cachedX.current) + oldWidth - newWidth
              }
              const newArticle = {
                ...values.article,
                areas
              }
              setFieldValue('article', newArticle)
            }}
            onBlur={handleBlur}
            isInvalid={!!(touched.article?.areas?.[0]?.width && errors.article?.areas?.[0]?.width)}
          />
          <InputGroup.Text>行幅</InputGroup.Text>
          <Form.Control.Feedback type='invalid'>{errors.article?.areas?.[0]?.width}</Form.Control.Feedback>
        </InputGroup>
      </Form.Group>
    </Row>
  )
}
